/**
 * The shape the specifications editor works in, and the two conversions around
 * it.
 *
 * `updateProduct.specifications` REPLACES the stored object — anything left out
 * of the input is cleared. So the form state has to round-trip every field it
 * read, including the ones the editor doesn't surface (see
 * `SpecDecorationRow.artworkUrl` / `.extraColors`).
 */
import { ProductDecorationMethod, type ProductSpecificationsInput } from '@codezo/graphql';
import {
	SPEC_FIELDS,
	getSpecField,
	type SpecColorLike,
	type SpecificationsLike,
} from './spec-fields';

/** One typed field the user has added. Only the members its `kind` uses are read. */
export interface SpecFieldRow {
	key: string;
	/** text / longtext / number / integer / measure value. */
	text: string;
	unit: string;
	tags: string[];
	length: string;
	width: string;
	height: string;
}

export interface SpecCustomRow {
	/** Local row identity — survives reordering, unlike the array index. */
	uid: string;
	/** Server id, kept so the spec stays stable across saves. */
	id?: string;
	label: string;
	value: string;
	unit: string;
	group: string;
}

export interface SpecColorRow {
	uid: string;
	name: string;
	code: string;
	pantone: string;
	appliesTo: string;
	finish: string;
	isPrimary: boolean;
}

export interface SpecDecorationRow {
	uid: string;
	id?: string;
	method: ProductDecorationMethod | '';
	methodNote: string;
	placement: string;
	colorCount: string;
	areaWidth: string;
	areaHeight: string;
	areaUnit: string;
	notes: string;
	/** Not editable here, but carried through so saving never drops it. */
	artworkUrl: string;
	/** Ditto for per-decoration colours. */
	extraColors: readonly SpecColorLike[] | null;
}

export interface SpecFormState {
	fields: SpecFieldRow[];
	custom: SpecCustomRow[];
	colors: SpecColorRow[];
	decorations: SpecDecorationRow[];
}

let uidSeq = 0;
export const nextSpecUid = () => `spec-row-${++uidSeq}`;

export const emptySpecForm = (): SpecFormState => ({
	fields: [],
	custom: [],
	colors: [],
	decorations: [],
});

export function makeSpecFieldRow(key: string): SpecFieldRow {
	const def = getSpecField(key);
	return {
		key,
		text: '',
		unit: def?.defaultUnit ?? '',
		tags: [],
		length: '',
		width: '',
		height: '',
	};
}

export const makeSpecCustomRow = (): SpecCustomRow => ({
	uid: nextSpecUid(),
	label: '',
	value: '',
	unit: '',
	group: '',
});

export const makeSpecColorRow = (): SpecColorRow => ({
	uid: nextSpecUid(),
	name: '',
	code: '',
	pantone: '',
	appliesTo: '',
	finish: '',
	isPrimary: false,
});

export const makeSpecDecorationRow = (): SpecDecorationRow => ({
	uid: nextSpecUid(),
	method: '',
	methodNote: '',
	placement: '',
	colorCount: '',
	areaWidth: '',
	areaHeight: '',
	areaUnit: 'cm',
	notes: '',
	artworkUrl: '',
	extraColors: null,
});

/* ════════════════════════ SERVER → FORM ════════════════════════ */

const num = (v: number | null | undefined) => (v == null ? '' : String(v));
const str = (v: string | null | undefined) => v ?? '';

export function specToForm(spec: SpecificationsLike | null | undefined): SpecFormState {
	const form = emptySpecForm();
	if (!spec) return form;

	/* Walk the registry so loaded fields land in a predictable order. */
	for (const def of SPEC_FIELDS) {
		const row = makeSpecFieldRow(def.key);
		let present = false;

		switch (def.key) {
			case 'material':
			case 'finish':
			case 'textureDescription': {
				const value = str(
					def.key === 'material'
						? spec.material
						: def.key === 'finish'
							? spec.finish
							: spec.textureDescription
				);
				if (value) {
					row.text = value;
					present = true;
				}
				break;
			}
			case 'secondaryMaterials':
			case 'features': {
				const tags =
					(def.key === 'secondaryMaterials' ? spec.secondaryMaterials : spec.features) ??
					[];
				if (tags.length > 0) {
					row.tags = [...tags];
					present = true;
				}
				break;
			}
			case 'capacity': {
				if (spec.capacity) {
					row.text = num(spec.capacity.value);
					row.unit = spec.capacity.unit || row.unit;
					present = true;
				}
				break;
			}
			case 'packaging.type':
			case 'packaging.notes': {
				const value = str(
					def.key === 'packaging.type' ? spec.packaging?.type : spec.packaging?.notes
				);
				if (value) {
					row.text = value;
					present = true;
				}
				break;
			}
			case 'packaging.dimensions': {
				const dims = spec.packaging?.dimensions;
				if (dims && (dims.length || dims.width || dims.height)) {
					row.length = num(dims.length);
					row.width = num(dims.width);
					row.height = num(dims.height);
					row.unit = dims.unit || row.unit;
					present = true;
				}
				break;
			}
			case 'packaging.weight': {
				if (spec.packaging?.weight != null) {
					row.text = num(spec.packaging.weight);
					row.unit = spec.packaging.weightUnit || row.unit;
					present = true;
				}
				break;
			}
		}

		if (present) form.fields.push(row);
	}

	form.colors = (spec.colors ?? []).map((c) => ({
		uid: nextSpecUid(),
		name: str(c.name),
		code: str(c.code),
		pantone: str(c.pantone),
		appliesTo: str(c.appliesTo),
		finish: str(c.finish),
		isPrimary: c.isPrimary ?? false,
	}));

	form.decorations = [...(spec.decorations ?? [])]
		.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
		.map((d) => ({
			uid: nextSpecUid(),
			id: d.id,
			method: d.method as ProductDecorationMethod,
			methodNote: str(d.methodNote),
			placement: str(d.placement),
			colorCount: num(d.colorCount),
			areaWidth: num(d.area?.width),
			areaHeight: num(d.area?.height),
			areaUnit: d.area?.unit || 'cm',
			notes: str(d.notes),
			artworkUrl: str(d.artworkUrl),
			extraColors: d.colors ?? null,
		}));

	form.custom = [...(spec.custom ?? [])]
		.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
		.map((c) => ({
			uid: nextSpecUid(),
			id: c.id,
			label: c.label,
			value: c.value,
			unit: str(c.unit),
			group: str(c.group),
		}));

	return form;
}

/* ════════════════════════ FORM → SERVER ════════════════════════ */

const trimmed = (v: string) => {
	const t = v.trim();
	return t.length > 0 ? t : undefined;
};

const toFloat = (v: string) => {
	const n = parseFloat(v);
	return Number.isFinite(n) ? n : undefined;
};

const toInt = (v: string) => {
	const n = parseInt(v, 10);
	return Number.isFinite(n) ? n : undefined;
};

/** `ProductDimensionsInput` requires all four members, so blanks become 0. */
const toDimensions = (row: SpecFieldRow, fallbackUnit: string) => {
	const length = toFloat(row.length);
	const width = toFloat(row.width);
	const height = toFloat(row.height);
	if (length == null && width == null && height == null) return undefined;
	return {
		length: length ?? 0,
		width: width ?? 0,
		height: height ?? 0,
		unit: row.unit || fallbackUnit,
	};
};

/**
 * Builds the replacement specification object. Returns `null` when the form
 * carries nothing, which clears the stored specification.
 */
export function formToSpecInput(form: SpecFormState): ProductSpecificationsInput | null {
	const field = (key: string) => form.fields.find((f) => f.key === key);
	const text = (key: string) => {
		const row = field(key);
		return row ? trimmed(row.text) : undefined;
	};
	const tags = (key: string) => {
		const row = field(key);
		if (!row) return undefined;
		const values = row.tags.map((t) => t.trim()).filter(Boolean);
		return values.length > 0 ? values : undefined;
	};

	const capacityRow = field('capacity');
	const capacityValue = capacityRow ? toFloat(capacityRow.text) : undefined;

	const packedWeightRow = field('packaging.weight');
	const packedWeight = packedWeightRow ? toFloat(packedWeightRow.text) : undefined;
	const packedDimsRow = field('packaging.dimensions');

	const packaging = {
		type: text('packaging.type'),
		dimensions: packedDimsRow ? toDimensions(packedDimsRow, 'cm') : undefined,
		weight: packedWeight,
		weightUnit: packedWeight == null ? undefined : packedWeightRow?.unit || 'g',
		notes: text('packaging.notes'),
	};

	const colors = form.colors
		.map((c) => ({
			name: trimmed(c.name),
			code: trimmed(c.code),
			pantone: trimmed(c.pantone),
			appliesTo: trimmed(c.appliesTo),
			finish: trimmed(c.finish),
			isPrimary: c.isPrimary || undefined,
		}))
		.filter((c) => c.name || c.code || c.pantone || c.appliesTo || c.finish);

	const decorations = form.decorations
		.filter((d) => d.method !== '')
		.map((d, index) => {
			const width = toFloat(d.areaWidth);
			const height = toFloat(d.areaHeight);
			return {
				id: d.id,
				method: d.method as ProductDecorationMethod,
				methodNote: trimmed(d.methodNote),
				placement: trimmed(d.placement),
				colorCount: toInt(d.colorCount),
				area:
					width == null && height == null
						? undefined
						: { width, height, unit: d.areaUnit || 'cm' },
				notes: trimmed(d.notes),
				artworkUrl: trimmed(d.artworkUrl),
				colors: d.extraColors?.map((c) => ({
					name: c.name,
					code: c.code,
					pantone: c.pantone,
					appliesTo: c.appliesTo,
					finish: c.finish,
					isPrimary: c.isPrimary,
				})),
				sortOrder: index,
			};
		});

	/* `label` and `value` are both required, so half-filled rows are dropped. */
	const custom = form.custom
		.filter((c) => c.label.trim() && c.value.trim())
		.map((c, index) => ({
			id: c.id,
			label: c.label.trim(),
			value: c.value.trim(),
			unit: trimmed(c.unit),
			group: trimmed(c.group),
			sortOrder: index,
		}));

	const hasPackaging = Object.values(packaging).some((v) => v !== undefined);

	const input: ProductSpecificationsInput = {
		material: text('material'),
		secondaryMaterials: tags('secondaryMaterials'),
		finish: text('finish'),
		textureDescription: text('textureDescription'),
		features: tags('features'),
		capacity:
			capacityValue == null
				? undefined
				: { value: capacityValue, unit: capacityRow?.unit || 'ml' },
		colors: colors.length > 0 ? colors : undefined,
		packaging: hasPackaging ? packaging : undefined,
		decorations: decorations.length > 0 ? decorations : undefined,
		custom: custom.length > 0 ? custom : undefined,
	};

	return Object.values(input).some((v) => v !== undefined) ? input : null;
}

/** How many specification entries the form carries — drives the tab badge. */
export function countSpecEntries(form: SpecFormState): number {
	const filled = form.fields.filter((row) => {
		const def = getSpecField(row.key);
		if (!def) return false;
		if (def.kind === 'tags') return row.tags.some((t) => t.trim());
		if (def.kind === 'dimensions') return !!(row.length || row.width || row.height);
		return !!row.text.trim();
	}).length;

	return (
		filled +
		form.colors.length +
		form.decorations.filter((d) => d.method !== '').length +
		form.custom.filter((c) => c.label.trim() && c.value.trim()).length
	);
}
